import { useQuery } from '@tanstack/react-query';
import { get, type ApiError } from './api';

/**
 * The small lookup lists every picker needs, typed once.
 *
 * Each screen used to fetch these itself, under keys of its own choosing — and
 * two of them chose the same key for different shapes, so whichever loaded
 * first decided what the other got: an empty SLA target grid on one path, a
 * crash on another. These hooks own the `['directory', …]` namespace; nothing
 * else should write into it.
 *
 * Five minutes stale: statuses and priorities change when an administrator
 * edits them, which is rare, and a list that stays put while somebody is
 * choosing from it is a feature.
 */

export interface DirectoryStatus {
  id: number;
  key: string;
  name: string;
  category: 'open' | 'pending' | 'resolved' | 'closed';
  position: number;
}

export interface DirectoryPriority {
  id: number;
  key: string;
  name: string;
  level: number;
}

export interface DirectoryGroup {
  id: number;
  name: string;
  description: string | null;
  workspace_id: number;
  assignment_strategy: string | null;
  member_count: number;
}

export interface DirectoryDepartment {
  id: number;
  name: string;
  parent_id: number | null;
}

export interface DirectoryLocation {
  id: number;
  name: string;
  city: string | null;
  country: string | null;
  timezone: string | null;
}

export interface DirectoryAgent {
  id: number;
  name: string;
  email: string;
  job_title: string | null;
  is_available: boolean | null;
  open_tickets: number | null;
}

const STALE = 5 * 60_000;

function useDirectory<T>(path: string, key: string, enabled = true) {
  return useQuery<{ data: T[] }, ApiError, T[]>({
    queryKey: ['directory', key],
    queryFn: () => get<{ data: T[] }>(path),
    select: (response) => response.data,
    staleTime: STALE,
    enabled,
  });
}

export const useStatuses = (enabled = true) => useDirectory<DirectoryStatus>('/directory/statuses', 'statuses', enabled);
export const usePriorities = (enabled = true) =>
  useDirectory<DirectoryPriority>('/directory/priorities', 'priorities', enabled);
export const useGroups = (enabled = true) => useDirectory<DirectoryGroup>('/directory/groups', 'groups', enabled);
export const useDepartments = (enabled = true) =>
  useDirectory<DirectoryDepartment>('/directory/departments', 'departments', enabled);
export const useLocations = (enabled = true) =>
  useDirectory<DirectoryLocation>('/directory/locations', 'locations', enabled);
/** Every active agent, for a select. For searching, use `PersonPicker` with `source="agents"`. */
export const useAgents = (enabled = true) => useDirectory<DirectoryAgent>('/directory/agents', 'agents', enabled);
